<template>
  <Preloader v-if="localLoading" />
  <OrderPlanSuccess :title="$t('changePlan')" v-else-if="success" />
  <div v-else-if="plan" class="plan-upgrade">
    <div class="plan-title">
      <RouterLink to="/settings/payment/plans" class="plan-title__link">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z"
            fill="black"
          />
        </svg>
      </RouterLink>
      <span>{{ $t('changePlan') }}</span>
    </div>
    <div class="plan-subtitle">
      {{
        $t('subject', {
          plan: plan.title,
          expirationText,
          nextPlan: nextPlan.title,
        })
      }}
      {{ formatCurrencyFromMoney(priceUpgrade) }}
    </div>
    <div class="plan-upgrade-wrap">
      <UpgradePaymentCalculating
        :plan="nextPlan"
        v-if="calculatingUpgrade"
        :calculating="calculatingUpgrade"
        :price="priceUpgrade"
      />
      <div class="plan-upgrade-after">
        <div class="plan-upgrade-after__text-top">
          {{ $t('after') }}
        </div>
        <div class="plan-upgrade-after__qt-wrap">
          <div class="plan-upgrade-after__qt-item" v-for="(value, name, index) in quotasUpgrade">
            <UpgradePaymentQuota :key="name" :type="name" :value="value" />
          </div>
        </div>
        <div class="plan-upgrade-after__text-bottom">
          <div>{{ $t('which', { endPeriodText }) }}.</div>
          <div>
            {{ $t('which2') }}
          </div>
        </div>
      </div>
    </div>
    <div class="plan-upgrade-bottom">
      {{ $t('from', { date: nextPaymentText }) }}
      {{ $tc('months', period.duration, { duration: period.duration }) }}
      {{ $t('from2', { price: formatCurrency(price, currency) }) }}
    </div>
    <PaymentMethodsSelector @input="onPaymentMethodChange" />
    <StripeUpgradePayment
      :plan="nextPlan"
      @success="onSuccess"
      :method-id="method"
      :new-card="needNewCard"
      :period="period"
      v-if="driver === paymentDrivers.STRIPE"
    />
    <AlfabankUpgradePayment
      :plan="nextPlan"
      @success="onSuccess"
      :method-id="method"
      :new-card="needNewCard"
      :period="period"
      v-if="driver === paymentDrivers.ALFABANK"
    />
  </div>
</template>

<script>
import StripeUpgradePayment from '@/components/Payment/Payment/Stripe/StripeUpgradePayment';
import usePayment from '@/compositions/accounting/usePayment';
import { orderPlanOperations, orderTypes, paymentDrivers } from '@/enums';
import useUpgradePlan from '@/compositions/accounting/useUpgradePlan';
import useNumberFormatter from '@/compositions/useNumberFormatter';
import useAccount from '@/compositions/accounting/useAccount';
import UpgradePaymentQuota from '@/components/Payment/Payment/UpgradePaymentQuota';
import { computed, ref, toRefs } from '@vue/composition-api';
import UpgradePaymentCalculating from '@/components/Payment/Payment/UpgradePaymentCalculating';
import PaymentMethodsSelector from '@/components/Payment/Methods/PaymentMethodsCardSelector';
import usePlan from '@/compositions/accounting/usePlan';
import Preloader from '@/components/Preloader';
import useValidation from '@/compositions/useValidation';
import OrderPlanSuccess from '@/components/Payment/Order/Plan/OrderPlanSuccess';
import usePaymentMethods from '@/compositions/accounting/usePaymentMethods';
import AlfabankUpgradePayment from '@/components/Payment/Payment/Alfabank/AlfabankUpgradePayment';

export default {
  name: 'PlanUpgrade',
  components: {
    AlfabankUpgradePayment,
    OrderPlanSuccess,
    Preloader,
    PaymentMethodsSelector,
    UpgradePaymentCalculating,
    UpgradePaymentQuota,
    StripeUpgradePayment,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
  },
  setup(props, { root: { $router } }) {
    const { id } = toRefs(props);
    const method = ref(null);
    const needNewCard = ref(null);
    const success = ref(false);

    const { account, expirationText, endPeriodText, nextPaymentText, period } = useAccount();
    const {
      priceUpgrade,
      quotasUpgrade,
      calculatingUpgrade,
      fetchData,
      calculateUpgrade,
    } = useUpgradePlan();

    const { paymentMethods, fetchAll: fetchMethods } = usePaymentMethods();

    const { withLoading, localLoading } = useValidation();

    withLoading(async () => {
      await fetchData();
      await calculateUpgrade(id.value);
      await fetchMethods();
      if (!paymentMethods.value?.length) {
        needNewCard.value = true;
      }
    });

    const { fetchAll, getPlanById } = usePlan();
    fetchAll();

    const { driver, setPeriod } = usePayment();

    const { formatCurrencyFromMoney, formatCurrency } = useNumberFormatter();

    function onPaymentMethodChange(val) {
      if (val !== 'new' && val) {
        method.value = val;
        setPeriod(period.value);
        needNewCard.value = false;
        return;
      }
      method.value = null;
      setPeriod(period.value);
      needNewCard.value = true;
    }

    const nextPlan = computed(() => getPlanById(id.value));
    const price = computed(
      () =>
        nextPlan.value.price.value *
        period.value.duration *
        ((100 - period.value.discountPercent) / 100),
    );

    function onSuccess() {
      success.value = true;
      $router.replace({
        name: 'order-success',
        params: {
          id: nextPlan.value.id,
          operation: orderPlanOperations.UPGRADE,
          type: orderTypes.PLAN,
        },
      });
    }

    return {
      driver,
      method,
      paymentDrivers,
      plan: account.value.plan,
      nextPlan,
      needNewCard,
      formatCurrencyFromMoney,
      formatCurrency,
      priceUpgrade,
      expirationText,
      quotasUpgrade,
      endPeriodText,
      nextPaymentText,
      period,
      price,
      currency: computed(() => nextPlan.value.price.currency),
      calculatingUpgrade,
      onPaymentMethodChange,
      localLoading,
      onSuccess,
      success,
    };
  },
};
</script>

<style lang="scss" scoped>
.plan-upgrade {
  font-weight: 400;
  padding: 2.125rem 2.5rem;
}
.plan-title {
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  line-height: 1.2;
  color: #212121;
  margin-bottom: 0.9375rem;
  &__link {
    width: 1.5rem;
    height: 1.5rem;
    margin-right: 0.5rem;
    svg {
      max-width: 100%;
      max-height: 100%;
    }
  }
}
.plan-subtitle {
  font-size: 1.0625rem;
  line-height: 1.53;
  color: #091a35;
  margin-bottom: 1.125rem;
}
.plan-upgrade-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  width: 100%;
  align-items: stretch;
  margin-bottom: 2.25rem;
}
.plan-upgrade-after {
  padding: 1.25rem 1.25rem 1.0625rem;
  background: #eeeeee;
  border-radius: 0.5rem;
  color: #091a35;
  @media screen and (max-width: 1704px) {
    width: 100%;
  }
  &__text-top {
    min-height: 2.5rem;
    line-height: 1.3;
    margin-bottom: 0.625rem;
  }
  &__qt-wrap {
    display: flex;
  }
  &__qt-item {
    margin: 0 1.875rem 1rem 0;
    &:last-child {
      margin-right: 0;
    }
  }
  &__text-bottom {
    font-size: 0.75rem;
    line-height: 1.25;
    color: rgb(9 26 53 / 50%);
  }
}
.plan-upgrade-bottom {
  margin-bottom: 1.25rem;
}

@media screen and (max-width: 1300px) {
  .plan-upgrade {
    padding: 1.25rem 0.625rem;
    &__btn {
      margin: 0 0.625rem;
    }
  }
  .plan-upgrade-wrap {
    display: block;
  }
  .plan-title {
    padding: 0 0.625rem;
    font-size: 1.25rem;
    &__link {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
  .plan-subtitle,
  .plan-upgrade-bottom {
    padding: 0 0.625rem;
    font-size: 0.9375rem;
    line-height: 1.39;
  }
}
@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .plan-upgrade-wrap {
    margin-bottom: 1.275rem;
  }
  .plan-upgrade-after {
    padding: 1.25rem 1.25rem 1.0625rem;
    &__text-top {
      min-height: inherit;
      font-size: 0.875rem;
      margin-bottom: 1rem;
    }
    &__qt-item {
      margin: 0 1.375rem 0.625rem 0;
    }
    &__text-bottom {
      font-size: 0.75rem;
      line-height: 1.25;
      color: rgb(9 26 53 / 50%);
    }
  }
}
@media screen and (max-width: 752px) {
  .plan-upgrade-after {
    padding: 0.9375rem 1.25rem 0.625rem;
    &__text-top {
      font-size: 0.8175rem;
      margin-bottom: 0.9375rem;
    }
    &__qt-wrap {
      justify-content: center;
    }
    &__qt-item {
      margin: 0 1.5rem 1.25rem 0;
    }
    &__text-bottom {
      font-size: 0.6875rem;
      line-height: 1.1;
    }
  }
}
@media #{map-get($display-breakpoints, 'xs-only')} {
  .plan-upgrade {
    padding: 1.25rem 0.625rem;
  }
  .plan-upgrade-wrap {
    margin-bottom: 1.25rem;
  }
  .plan-title {
    padding: 0 0.625rem;
    font-size: 1rem;
    margin-bottom: 0.625rem;
    &__link {
      width: 1rem;
      height: 1rem;
    }
  }
  .plan-subtitle,
  .plan-upgrade-bottom {
    font-size: 0.8125rem;
    line-height: 1.39;
    margin-bottom: 0.75rem;
  }
}
</style>

<i18n lang="yml" locale="ru">
changePlan: Изменение тарифного плана
subject: 'У вас оплачен тариф «{plan}» до {expirationText}. Чтобы перейти на тариф «{nextPlan}», необходимо доплатить {price}'
after: 'При переходе вам будет начислено:'
which: 'Действительны до {endPeriodText}'
which2: 'Далее списания будут происходить согласно выбранному тарифному плану.'
from: 'Начиная с {date} с вашей карты каждые {duration}'
months: '{duration} месяцев | {duration} месяц | {duration} месяца | {duration} месяцев'
from2: 'будет списываться сумма {price}, пока вы не отмените подписку.'
next: Далее
</i18n>
<i18n lang="yml" locale="en">
changePlan: Change plan
subject: "You have “{plan}” paid until {expirationText}. To switch to ”{nextPlan}”, you need to pay {price}."
after: 'Once your plan has been upgraded, you will immediately be credited '
which: 'Which will be valid until {endPeriodText}'
which2: 'Further charges will take place according to your payment period.'
from: 'Starting from {date} your card will be charged {price} every {duration}'
months: '{duration} months | {duration} month | {duration} months'
from2: 'until you cancel the subscription.'
next: Next
</i18n>
